import * as React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { PageSection } from '../../../../components/layout/PageSection';
import { AmountDisplay } from '../../../../components/ui/AmountDisplay';
import { useBalanceSheet } from '../../../../features/reports/client';
import { Button } from '../../../../components/ui/Button';
import { Input } from '../../../../components/ui/FormField';
import { PageHeader } from '../../../../components/layout/PageHeader';
import { Skeleton } from '../../../../components/ui/Skeleton';

export const Route = createFileRoute('/_auth/reports/financial/balance-sheet')({
  component: BalanceSheetPage,
});

function BalanceSheetPage() {
  const [asOfDate, setAsOfDate] = React.useState('2026-06-22');
  const { data, isLoading, isError, refetch } = useBalanceSheet(asOfDate);

  return (
    <div className="flex flex-col w-full h-full" style={{ viewTransitionName: 'page-body' }}>
      <PageHeader
        title="Balance Sheet"
        subtitle="Financial statement summarizing assets, liabilities, and shareholders' equity at a specific point in time."
        action={null}
      />
      <div className="mb-4">
        <div className="flex gap-4 items-center">
          <span>As of Date:</span>
          <Input 
            type="date" 
            className="w-auto" 
            value={asOfDate}
            onChange={(e) => setAsOfDate(e.target.value)}
          />
        </div>
      </div>
      <div className="flex-1 w-full min-h-0">
        {(() => {
          if (isLoading) return <Skeleton rows={5} />;
          if (isError) return (
            <div className="p-8 text-center">
              <p className="text-sm font-semibold text-danger-600">Failed to load balance sheet</p>
              <Button variant="secondary" size="sm" className="mt-3" onClick={() => refetch()}>Retry</Button>
            </div>
          );
          return (
            <>
              <div className={`px-4 py-3 rounded mb-6 flex justify-between font-bold border ${data?.isBalanced ? 'bg-success-bg border-success-bg text-success-text' : 'bg-danger-bg border-danger-bg text-danger-text'}`}>
                <span>Equation Validation:</span>
                <span>Assets (<AmountDisplay paise={data?.totalAssets || 0} />) = Liabilities & Equity (<AmountDisplay paise={data?.totalLiabilitiesAndEquity || 0} />)</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <PageSection title="Assets">
                  <table className="w-full text-left">
                    <tbody>
                      {data?.assets.map((item: any) => (
                        <tr key={item.category}>
                          <td className="py-2">{item.category}</td>
                          <td className="py-2 text-right"><AmountDisplay paise={item.amount} /></td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="border-t border-border font-bold text-lg">
                      <tr>
                        <td className="py-4">Total Assets</td>
                        <td className="py-4 text-right">
                          <AmountDisplay paise={data?.totalAssets || 0} />
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </PageSection>

                <PageSection title="Liabilities & Equity">
                  <div className="mb-4">
                    <h3 className="font-semibold text-text-secondary mb-2">Liabilities</h3>
                    <table className="w-full text-left">
                      <tbody>
                        {data?.liabilities.map((item: any) => (
                          <tr key={item.category}>
                            <td className="py-2">{item.category}</td>
                            <td className="py-2 text-right"><AmountDisplay paise={item.amount} /></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div>
                    <h3 className="font-semibold text-text-secondary mb-2">Equity</h3>
                    <table className="w-full text-left">
                      <tbody>
                        {data?.equity.map((item: any) => (
                          <tr key={item.category}>
                            <td className="py-2">{item.category}</td>
                            <td className="py-2 text-right"><AmountDisplay paise={item.amount} /></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <table className="w-full text-left border-t border-border mt-4 font-bold text-lg">
                    <tbody>
                      <tr>
                        <td className="py-4">Total Liabilities & Equity</td>
                        <td className="py-4 text-right">
                          <AmountDisplay paise={data?.totalLiabilitiesAndEquity || 0} />
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </PageSection>
              </div>
            </>
          );
        })()}
      </div>
    </div>
  );
}
